import { Navigate, useLocation } from "react-router-dom";
import { useEffect, type ReactElement } from "react";
import { useAuth } from "../hooks/useAuth";
import { SessionBootScreen } from "../components/boot/SessionBootScreen";
import { prefetchHomeChunk } from "./routePrefetch";

interface ProtectedRouteProps {
  children: ReactElement;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isAnonymous, isLoading, bootFailure } = useAuth();
  const location = useLocation();

  // Deep link com sessão (atalho da PWA em `/home`, push, link de livro): a
  // Home é o destino mais provável do próximo toque — baixa o chunk junto com
  // o perfil, não depois dele. Ver `routePrefetch.ts`.
  const hasSession = !!user && !isAnonymous;
  useEffect(() => {
    if (hasSession) prefetchHomeChunk();
  }, [hasSession]);

  // Boot estourado: sem isto, quem entra por deep link numa rota protegida
  // (`/home` do atalho, link de livro) cai no `return null` abaixo e vê uma
  // tela VAZIA para sempre — o mesmo beco do spinner, sem nem o spinner.
  if (bootFailure) {
    return <SessionBootScreen />;
  }

  // `isLoading` só é `true` no BOOT (e no retry) — nunca entre navegações
  // protegidas, que era o medo do `return null` antigo. O que o vazio
  // produzia na prática: recarregar `/home` no navegador (ou abrir um push
  // com deep link) mostrava tela EM BRANCO durante o boot inteiro, enquanto
  // a `/` tinha marca, barra e diagnóstico. Mesma tela de abertura nos dois
  // caminhos (17/09).
  if (isLoading) {
    return <SessionBootScreen />;
  }

  // F1.5 do Roadmap PRO (11/09): sessão anônima (visitante de perfil público
  // sem conta) conta como "sem sessão" aqui — é o que impede um visitante de
  // alcançar QUALQUER rota Guarded (Home, Settings, /pro, /admin...) com uma
  // sessão que não é uma conta de verdade. Só `AnonymousOrGuarded` admite
  // sessão anônima, de propósito, para a própria página de perfil.
  if (!user || isAnonymous) {
    // O destino viaja no state pro `/login` devolver o usuário onde ele
    // pediu pra estar. Sem isto, quem abre um link de livro compartilhado
    // deslogado é logado e largado na Home, sem o livro — e como os dois
    // saltos são `replace`, sobra UMA entrada no histórico: o back também
    // saía do app. É a superfície de aquisição do produto.
    //
    // `replace` continua: a URL protegida não pode ficar no histórico atrás
    // do login, senão o back do sistema volta pro guard e rebate pro /login.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}
